/**
 * pageSetupBridge — PDF-6.9a Page Setup Manager bridge.
 *
 * Pure helpers that translate between the model's authoritative
 * {@link LayoutPlotSettings} (AcDbPlotSettings sub-object) and the
 * {@link PageSetupDraft} the {@link PageSetupManagerDialog} edits.
 *
 * # Design choice
 *
 * The existing PRINT dialog (`usePrintDialog`) has its own narrow
 * `dialogToPlotSettings` / `plotSettingsToDialog` pair limited to the
 * 4 fields the PRINT page-setup surface exposes (paperSize / orientation
 * / margins / area). PDF-6.9a's Page Setup Manager edits the FULL
 * {@link LayoutPlotSettings} (rotation, offset, plot-style, scale, all
 * three lineweight/center flag bits) so we ship a SEPARATE bridge here
 * rather than overloading `dialogToPlotSettings` — keeping the smaller
 * PRINT-dialog bridge intact and the FULL page-setup bridge focused on
 * the manager's surface. `dialogToPlotSettings` continues to be the
 * canonical bridge for the PRINT dialog; this module is the canonical
 * bridge for the Page Setup Manager.
 *
 * # AutoCAD parity / DXF semantics
 *
 *  - **Paper size + orientation** — the dialog exposes the catalogue
 *    {@link PaperSizeId} ids; landscape is encoded by `paperWidthMm >
 *    paperHeightMm` (mirrors `usePrintDialog`'s convention).
 *  - **Margins** — DXF codes 40/41/42/43 ↔ `margins`. The model uses
 *    `{ left, bottom, right, top }`; the draft surfaces them as a
 *    `{ left, right, top, bottom }` map (matches the PRINT dialog UX).
 *  - **Plot area** — exposes Layout / Extents / Window. Window is
 *    read-only in PDF-6.9a (the interactive Window-pick belongs to
 *    PDF-6.7's PRINT dialog); the manager preserves any persisted
 *    window codes verbatim so the round-trip stays lossless.
 *  - **Scale** — the dialog offers a preset list (1:1, 1:2, 1:5, 1:10,
 *    1:20, 1:50, 1:100, 1:200, 1:500, 1:1000, Fit to paper) plus a
 *    "custom" mode that opens the numerator/denominator inputs. "Fit
 *    to paper" is encoded as `scale = { numerator: 0, denominator: 1 }`
 *    — the AutoCAD legacy convention for "scaled to fit" (group 75 = 0,
 *    no UseStandardScale flag bit). When AutoCAD reads back a 0
 *    numerator it knows to recompute the scale to fit the chosen plot
 *    area on the paper.
 *  - **Rotation** — DXF code 73 ↔ {@link PlotRotation} enum.
 *  - **Offset** — DXF codes 46/47 ↔ `offset` (mm).
 *  - **Plot-style sheet** — DXF code 7 ↔ `plotStyleName`. Empty string
 *    = "None" (no CTB/STB). When a non-empty name is already on the
 *    model (imported from DXF), the dropdown surfaces THAT name as a
 *    second option so round-trip preserves it; CTB/STB enumeration is
 *    deferred to a later slice (PDF-6.3a/b).
 *  - **Flag bits** — DXF code 70 bit field:
 *      4 (PlotCentered)        ↔ `centerPlot`
 *      64 (ScaleLineweights)   ↔ `scaleLineweights`
 *      128 (PrintLineweights)  ↔ `printLineweights`
 *    All OTHER bits on `flags` are preserved verbatim so the
 *    round-trip is lossless for unrelated flag bits the manager does
 *    not edit (PlotViewportBorders / ShowPlotStyles / PlotHidden /
 *    UseStandardScale / PlotPlotStyles / DrawViewportsFirst / ModelType).
 *
 * The mapping is its own pure module so it is trivially unit-testable
 * without React / Zustand.
 */

import {
  PlotAreaType,
  PlotLayoutFlags,
  PlotPaperUnits,
  PlotRotation,
  type LayoutPlotSettings,
  type PlotMargins,
  type PlotOffset,
  type PlotScale,
  type PlotWindow,
} from "@dxf-ai-editor/drawing-model";

import {
  resolvePaperDimensions,
  type PaperSizeId,
  type PrintArea,
  type PrintMargins,
  type PrintOrientation,
} from "./PrintPreviewDialog";
import { resolveDialogPaperSize } from "../hooks/usePrintDialog";

/**
 * Page-setup draft — the fully-editable form state for one layout in
 * the Page Setup Manager. All fields are surfaced as primitives /
 * small structs so the UI binds to them directly without indirection.
 */
export interface PageSetupDraft {
  /** Layout space id — the unambiguous lookup key used by the store. */
  readonly spaceId: string;
  /**
   * Layout's display name (e.g. "Layout1"). Required by the shell's
   * `T extends { name: string }` constraint. Read-only in the manager
   * — renaming layouts is a separate command.
   */
  readonly name: string;

  // ── Paper / orientation ──
  readonly paperSize: PaperSizeId;
  readonly orientation: PrintOrientation;

  // ── Plot area ──
  readonly area: PrintArea;

  // ── Scale ──
  /**
   * True iff the draft uses "Fit to paper" mode (`scale.numerator === 0`).
   * When true, the numerator/denominator inputs are disabled and the
   * dialog renders the "Fit to paper" badge.
   */
  readonly fitToPaper: boolean;
  /** Custom-scale numerator (paper / real-world units). */
  readonly scaleNumerator: number;
  /** Custom-scale denominator (drawing units). */
  readonly scaleDenominator: number;

  // ── Plot style ──
  readonly plotStyleName: string;

  // ── Rotation ──
  readonly rotation: PlotRotation;

  // ── Offset (mm) ──
  readonly offsetX: number;
  readonly offsetY: number;

  // ── Margins (mm) — { left, right, top, bottom } ──
  readonly margins: PrintMargins;

  // ── Flag-bit booleans ──
  readonly centerPlot: boolean;
  readonly scaleLineweights: boolean;
  readonly printLineweights: boolean;

  // ── Preserved fields ──
  /** Window rect (codes 48/49/140/141) — preserved verbatim. PDF-6.9a does NOT edit it. */
  readonly window: PlotWindow;
  /** DXF code 1 page-setup name — preserved verbatim. */
  readonly pageSetupName: string;
  /** DXF code 72 paper units — preserved verbatim (always mm in our writes). */
  readonly units: PlotPaperUnits;
  /**
   * The original `flags` bit field at draft-construction time, with the
   * three editable bits (4 / 64 / 128) cleared. Holds the bits the
   * manager does NOT edit so we can OR them back in on apply without
   * losing fidelity.
   */
  readonly preservedFlagBits: number;
}

/** Mask of the three flag bits the Page Setup Manager edits (4 | 64 | 128). */
const EDITABLE_FLAG_MASK: number =
  PlotLayoutFlags.PlotCentered |
  PlotLayoutFlags.ScaleLineweights |
  PlotLayoutFlags.PrintLineweights;

/** Mask of the bits NOT edited by the manager — preserved verbatim. */
const PRESERVED_FLAG_MASK: number = ~EDITABLE_FLAG_MASK & 0xffffffff;

/**
 * Map a {@link PlotAreaType} to the dialog's plot-area choice. Mirrors
 * `usePrintDialog.plotAreaTypeToDialogArea` semantics: Layout / Extents /
 * Window pass straight through; Display / Limits / View fold onto the
 * nearest non-Layout / non-Window choice (Extents). The full enum is
 * preserved on the model's `plotSettings`, so the read-only Window
 * payload is intact for round-trips.
 */
function plotAreaTypeToArea(type: PlotAreaType): PrintArea {
  switch (type) {
    case PlotAreaType.Window:
      return "window";
    case PlotAreaType.Layout:
      return "layout";
    case PlotAreaType.Extents:
    case PlotAreaType.Display:
    case PlotAreaType.Limits:
    case PlotAreaType.View:
    default:
      return "extents";
  }
}

/** Inverse of {@link plotAreaTypeToArea}. */
function areaToPlotAreaType(area: PrintArea): PlotAreaType {
  switch (area) {
    case "extents":
      return PlotAreaType.Extents;
    case "window":
      return PlotAreaType.Window;
    case "layout":
    default:
      return PlotAreaType.Layout;
  }
}

/**
 * Project a {@link LayoutPlotSettings} onto a {@link PageSetupDraft}.
 * Lossless for the fields the manager edits + the preserved-flag-bits
 * blob; the {@link window} payload is preserved verbatim so callers can
 * re-emit it via {@link settingsFromPageSetupDraft}.
 *
 * `spaceId` and `name` come from the surrounding orchestration (the
 * model identifies layouts by space id, not by their plot-settings
 * struct), so the caller supplies them.
 */
export function pageSetupDraftFromSettings(
  settings: LayoutPlotSettings,
  spaceId: string,
  name: string,
): PageSetupDraft {
  const orientation: PrintOrientation =
    settings.paperWidthMm > settings.paperHeightMm ? "landscape" : "portrait";
  const fitToPaper = settings.scale.numerator === 0;
  return {
    spaceId,
    name,
    paperSize: resolveDialogPaperSize(settings.paperName),
    orientation,
    area: plotAreaTypeToArea(settings.plotAreaType),
    fitToPaper,
    // When fitToPaper, keep numerator/denominator inputs at the safe
    // editable defaults (1/1) so the user can switch back to Custom
    // without seeing 0/1 in the inputs. The real scale persisted on
    // apply is { 0, 1 } via fitToPaper.
    scaleNumerator: fitToPaper ? 1 : settings.scale.numerator,
    scaleDenominator: fitToPaper ? 1 : settings.scale.denominator,
    plotStyleName: settings.plotStyleName,
    rotation: settings.rotation,
    offsetX: settings.offset.x,
    offsetY: settings.offset.y,
    margins: {
      left: settings.margins.left,
      right: settings.margins.right,
      top: settings.margins.top,
      bottom: settings.margins.bottom,
    },
    centerPlot: (settings.flags & PlotLayoutFlags.PlotCentered) !== 0,
    scaleLineweights:
      (settings.flags & PlotLayoutFlags.ScaleLineweights) !== 0,
    printLineweights:
      (settings.flags & PlotLayoutFlags.PrintLineweights) !== 0,
    window: { ...settings.window },
    pageSetupName: settings.pageSetupName,
    units: settings.units,
    preservedFlagBits: settings.flags & PRESERVED_FLAG_MASK,
  };
}

/**
 * Inverse of {@link pageSetupDraftFromSettings}: project a draft back
 * onto the model's {@link LayoutPlotSettings}. Lossless for every
 * field this module documents — the preserved-flag-bits blob is
 * OR'd back in so unrelated bit-field flags survive the round-trip.
 */
export function settingsFromPageSetupDraft(
  draft: PageSetupDraft,
): LayoutPlotSettings {
  const paper = resolvePaperDimensions(draft.paperSize, draft.orientation);
  const margins: PlotMargins = {
    left: draft.margins.left,
    right: draft.margins.right,
    top: draft.margins.top,
    bottom: draft.margins.bottom,
  };
  const scale: PlotScale = draft.fitToPaper
    ? { numerator: 0, denominator: 1 }
    : {
        numerator: draft.scaleNumerator,
        denominator: draft.scaleDenominator,
      };
  const offset: PlotOffset = { x: draft.offsetX, y: draft.offsetY };
  // Reassemble flags: preserved bits + the three editable bits set per
  // the draft booleans.
  let flags = draft.preservedFlagBits & PRESERVED_FLAG_MASK;
  if (draft.centerPlot) flags |= PlotLayoutFlags.PlotCentered;
  if (draft.scaleLineweights) flags |= PlotLayoutFlags.ScaleLineweights;
  if (draft.printLineweights) flags |= PlotLayoutFlags.PrintLineweights;
  return {
    pageSetupName: draft.pageSetupName,
    paperName: draft.paperSize,
    paperWidthMm: paper.width,
    paperHeightMm: paper.height,
    units: draft.units,
    margins,
    plotAreaType: areaToPlotAreaType(draft.area),
    window: { ...draft.window },
    scale,
    offset,
    rotation: draft.rotation,
    plotStyleName: draft.plotStyleName,
    flags,
  };
}

/**
 * Canonical scale presets surfaced in the Page Setup Manager's scale
 * dropdown. Each entry is a stable id + the numerator / denominator
 * pair the preset commits when chosen. `"fit"` is the sentinel for
 * Fit-to-paper (which writes `{ 0, 1 }`); `"custom"` is the sentinel
 * indicating the user-entered numerator/denominator inputs are active.
 */
export interface ScalePreset {
  readonly id: string;
  readonly numerator: number;
  readonly denominator: number;
}

export const FIT_TO_PAPER_PRESET_ID = "fit" as const;
export const CUSTOM_SCALE_PRESET_ID = "custom" as const;

export const SCALE_PRESETS: readonly ScalePreset[] = [
  { id: FIT_TO_PAPER_PRESET_ID, numerator: 0, denominator: 1 },
  { id: "1:1", numerator: 1, denominator: 1 },
  { id: "1:2", numerator: 1, denominator: 2 },
  { id: "1:5", numerator: 1, denominator: 5 },
  { id: "1:10", numerator: 1, denominator: 10 },
  { id: "1:20", numerator: 1, denominator: 20 },
  { id: "1:50", numerator: 1, denominator: 50 },
  { id: "1:100", numerator: 1, denominator: 100 },
  { id: "1:200", numerator: 1, denominator: 200 },
  { id: "1:500", numerator: 1, denominator: 500 },
  { id: "1:1000", numerator: 1, denominator: 1000 },
];

/**
 * Identify which preset id (if any) matches the draft's current
 * scale. Falls back to {@link CUSTOM_SCALE_PRESET_ID} when no preset
 * matches — that's the case for imported DXFs with bespoke ratios
 * (e.g. 1:75) or any manual edit through the inputs.
 */
export function detectScalePresetId(draft: PageSetupDraft): string {
  if (draft.fitToPaper) return FIT_TO_PAPER_PRESET_ID;
  for (const preset of SCALE_PRESETS) {
    if (preset.id === FIT_TO_PAPER_PRESET_ID) continue;
    if (
      preset.numerator === draft.scaleNumerator &&
      preset.denominator === draft.scaleDenominator
    ) {
      return preset.id;
    }
  }
  return CUSTOM_SCALE_PRESET_ID;
}

/**
 * Apply a scale-preset selection to a draft, returning the new draft.
 * - {@link FIT_TO_PAPER_PRESET_ID} → fitToPaper = true, scale inputs
 *   reset to 1/1 (safe defaults so a later "Custom" switch is clean).
 * - {@link CUSTOM_SCALE_PRESET_ID} → fitToPaper = false, numerator /
 *   denominator preserved (the user is about to edit them).
 * - any concrete preset → fitToPaper = false, numerator/denominator
 *   taken from the preset.
 */
export function applyScalePresetToDraft(
  draft: PageSetupDraft,
  presetId: string,
): PageSetupDraft {
  if (presetId === FIT_TO_PAPER_PRESET_ID) {
    return { ...draft, fitToPaper: true, scaleNumerator: 1, scaleDenominator: 1 };
  }
  if (presetId === CUSTOM_SCALE_PRESET_ID) {
    return { ...draft, fitToPaper: false };
  }
  const preset = SCALE_PRESETS.find((p) => p.id === presetId);
  if (!preset) return draft;
  return {
    ...draft,
    fitToPaper: false,
    scaleNumerator: preset.numerator,
    scaleDenominator: preset.denominator,
  };
}
